// Draws the roads between places on the world map.
//
// The map markers already know *where* every place sits (a percentage x/y over
// the map image); what the picture never showed was which places a road
// actually joins. This turns a route into an SVG path the WorldMapScreen can
// ink under the pins.
//
// The paths are deliberately not straight. A ruled line between two pins reads
// as a diagram; a real road wanders. So each route is drawn as a gentle,
// *deterministic* wander: five points from end to end, the three interior ones
// nudged to either side of the straight line, then smoothed into one continuous
// curve. The wander is seeded off the route id, so a given road looks the same
// every render and across sessions — it is scenery, not noise.

import type { LocationPosition } from "../types"

/** FNV-1a — a small, stable string hash to seed a road's wander from its id. */
function seedFrom(text: string): number {
  let hash = 2166136261
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/** mulberry32 — a tiny deterministic PRNG, so the same seed always wanders the same way. */
function makeRng(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const round = (n: number) => Math.round(n * 100) / 100

/**
 * Smooths a run of points into one continuous cubic path using a Catmull-Rom
 * spline (converted to Béziers). The curve passes *through* every point, so the
 * road visibly threads the waypoints rather than merely being pulled toward
 * them — and neighbouring segments meet without a kink.
 */
function smoothPath(points: LocationPosition[]): string {
  if (points.length < 2) return ""
  let d = `M ${round(points[0].x)} ${round(points[0].y)}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? p2
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = p1.y + (p2.y - p0.y) / 6
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${round(c1x)} ${round(c1y)}, ${round(c2x)} ${round(c2y)}, ${round(p2.x)} ${round(p2.y)}`
  }
  return d
}

/**
 * The SVG `d` for the road joining two places. `seed` (the route id) fixes the
 * wander so the road is stable; endpoints stay pinned exactly on the two
 * markers, and only the middle of the road strays.
 */
export function connectionPath(
  from: LocationPosition,
  to: LocationPosition,
  seed: string,
): string {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.hypot(dx, dy) || 1
  // Unit vector perpendicular to the straight line — the axis the road wanders along.
  const perpX = -dy / length
  const perpY = dx / length
  // Wander gently, and never more than a few map-percent even on long roads,
  // so the curve stays legibly a connection rather than a detour.
  const amplitude = Math.min(Math.max(length * 0.09, 1.4), 5)
  const rng = makeRng(seedFrom(seed))

  const COUNT = 5
  const points: LocationPosition[] = []
  for (let i = 0; i < COUNT; i++) {
    const t = i / (COUNT - 1)
    const baseX = from.x + dx * t
    const baseY = from.y + dy * t
    // Ends are pinned to the markers; the interior tapers off toward them
    // (sin is 0 at both ends, 1 in the middle) so roads leave and arrive clean.
    const isEnd = i === 0 || i === COUNT - 1
    const offset = isEnd ? 0 : (rng() * 2 - 1) * amplitude * Math.sin(Math.PI * t)
    points.push({ x: baseX + perpX * offset, y: baseY + perpY * offset })
  }
  return smoothPath(points)
}
